import { ServiceClient, TokenFunc, Call, ServiceError } from "@securityjourney/service-client";
import { getCurrentSelectedTenant, retrieveAuthToken } from "../utils";

// Types derived from svc-guardian OpenAPI spec (docs/openapi.yaml)

export type ScannerType = "snyk" | "bandit" | "sonarqube" | "semgrep";
export type ScanStatus = "success" | "error";

export type ScannersResponse = {
  supported_scanners: ScannerType[];
  total_count: number;
};

export type ScansByScanner = Partial<Record<ScannerType, number>>;

export type TokenUsage = {
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  avg_tokens_per_request: number;
};

export type ScanMetricsSummary = {
  total_scans: number;
  scans_with_vulnerabilities: number;
  instructions_updated_count: number;
  total_vulnerability_count: number;
  failed_scans: number;
  scans_by_scanner: ScansByScanner;
  // SJ admin-only fields
  successful_scans?: number;
  success_rate?: number;
  avg_response_time_ms?: number;
  scans_by_tenant?: Record<string, number>;
  token_usage?: TokenUsage;
};

export type MetricsSummaryResponse = {
  period_days: number;
  scanner_type_filter: ScannerType | null;
  scan_metrics: ScanMetricsSummary;
};

export type ScanRecord = {
  id: number;
  scanner_type: ScannerType;
  status: ScanStatus;
  vulnerability_count: number;
  vulnerabilities: object[] | null;
  created_at: string;
  original_instructions?: string | null;
  updated_instructions?: string | null;
  // SJ admin-only fields
  response_time_ms?: number | null;
  endpoint?: string | null;
  error_message?: string | null;
  extra_data?: object | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  total_tokens?: number | null;
};

export type ScanHistoryResponse = {
  total: number;
  limit: number;
  offset: number;
  scans: ScanRecord[];
};

// Re-export a dummy paths type to satisfy ServiceClient's generic — types above are used directly

type GuardianPaths = Record<string, any>;

export class GuardianClient extends ServiceClient<GuardianPaths> {
  constructor(getToken: TokenFunc, url?: string) {
    super({ getToken, url });
  }

  getScanners(): Call<ScannersResponse> {
    const func = this.fetcher.path("/svc/guardian/scanners").method("get").create();

    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func({}, init))
        .then(res => res.data as ScannersResponse)
        .catch(ServiceError.raise),
    };
  }

  getMetrics(req?: { days?: number; scannerType?: ScannerType }): Call<MetricsSummaryResponse> {
    const func = this.fetcher.path("/svc/guardian/{tenant}/metrics").method("get").create();
    const args = {
      tenant: getCurrentSelectedTenant(),
      days: req?.days,
      scanner_type: req?.scannerType,
    };

    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func(args, init))
        .then(res => res.data as MetricsSummaryResponse)
        .catch(ServiceError.raise),
    };
  }

  getScanHistory(req?: {
    limit?: number;
    offset?: number;
    scannerType?: ScannerType;
    status?: ScanStatus;
    dateFrom?: string;
    dateTo?: string;
    sortOrder?: "asc" | "desc";
  }): Call<ScanHistoryResponse> {
    const func = this.fetcher.path("/svc/guardian/{tenant}/metrics/scans").method("get").create();
    const args = {
      tenant: getCurrentSelectedTenant(),
      limit: req?.limit,
      offset: req?.offset,
      scanner_type: req?.scannerType,
      status: req?.status,
      date_from: req?.dateFrom,
      date_to: req?.dateTo,
      sort_order: req?.sortOrder,
    };

    const { init, abort } = this.withAbort();
    return {
      abort,
      promise: this.withAuthorization(init)
        .then(init => func(args, init))
        .then(res => res.data as ScanHistoryResponse)
        .catch(ServiceError.raise),
    };
  }
}

let guardianClientInstance: GuardianClient | null = null;

export const getGuardianClient = (): GuardianClient => {
  if (!guardianClientInstance) {
    guardianClientInstance = new GuardianClient(retrieveAuthToken);
  }
  return guardianClientInstance;
};
